<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('options.editImages')"></page-title>
    </div>

    <jet-form-section>

        <template #form>

            <modal-content>

                <div class="flex px-2 justify-between items-center pb-3 mb-4 border-b">

                    <h3>{{ localImages.length }} {{ $t("options.of3ImagesUploaded") }}<br />
                        <small class="text-gray-500">{{ $t("options.maxFilesize", { size: maxSizeInMb }) }}</small></h3>

                    <div class="flex items-center space-x-4">
                        <primary-button :disabled="localImages.length > 2" type="button" @click.prevent="selectNewPhoto">
                            <CloudUploadIcon class="-ml-1 mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                            {{ $t("options.addImage") }}
                        </primary-button>
                    </div>

                    <input type="file" class="hidden" ref="image" accept="image/png, image/jpeg, image/heic"
                           @change="handleFileSelect" />

                </div>
                <div class="flex">
                    <option-image v-for="image in localImages"
                                  :image="image"
                                  @deleteImage="deleteImage($event)"
                                  @setDefault="setDefault($event)"
                                  :key="image.uuid" />

                    <p v-if="localImages.length === 0" class="justify-self-center text-center w-full p-3 text-gray-500">
                        {{ $t("options.noImageUploaded") }}</p>
                </div>

            </modal-content>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">
                <primary-button class="ml-3 " @click="cancel">
                    {{ $t("actions.close") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

    <!-- Crop Modal -->
    <jet-dialog-modal :show="showCropDialog" @close="closeCropDialog" max-width="4xl">
        <template #title>
            <div class="flex justify-between items-center w-full">
                <span>{{ $t('crop.cropYourImage') }}</span>
                <div class="flex items-center">
                    <span class="mr-3 text-xl font-bold text-gray-800">{{ $t('crop.crop') }}</span>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" v-model="cropToggle" class="sr-only peer">
                        <div class="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-minplan-blue/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-minplan-blue"></div>
                    </label>
                </div>
            </div>
        </template>
        <template #content>
            <div class="flex flex-col items-center">
                <p class="text-sm text-gray-500 mb-4">{{ $t('crop.cropInstructions') }}</p>
                <div class="relative bg-gray-100 rounded-lg shadow-inner overflow-auto border border-gray-200" style="max-height: 75vh;">
                    <div class="relative inline-block min-w-full">
                        <img :src="cropImageSrc" ref="cropImage" class="max-w-full h-auto block mx-auto" @load="initCropBox" />
                        <div
                            v-if="cropImageSrc && cropToggle"
                            class="absolute border-2 border-dashed border-white shadow-[0_0_0_9999px_rgba(0,0,0,0.65)] z-10 cursor-move transition-shadow duration-300"
                            :style="{
                                left: cropBox.x + 'px',
                                top: cropBox.y + 'px',
                                width: displayCropWidth + 'px',
                                height: displayCropHeight + 'px',
                                outline: '1px solid rgba(0,0,0,0.3)'
                            }"
                            @mousedown="startDragging"
                        >
                            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div class="w-8 h-8 border-2 border-white/30 rounded-full flex items-center justify-center">
                                    <div class="w-1 h-1 bg-white rounded-full"></div>
                                </div>
                            </div>
                            <!-- Resize Handles -->
                            <div class="absolute -top-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nwse-resize z-20 hover:scale-125 transition-transform" @mousedown.stop="startResizing('nw', $event)"></div>
                            <div class="absolute -top-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nesw-resize z-20 hover:scale-125 transition-transform" @mousedown.stop="startResizing('ne', $event)"></div>
                            <div class="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nesw-resize z-20 hover:scale-125 transition-transform" @mousedown.stop="startResizing('sw', $event)"></div>
                            <div class="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-white border-2 border-minplan-blue rounded-full cursor-nwse-resize z-20 hover:scale-125 transition-transform" @mousedown.stop="startResizing('se', $event)"></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <jet-secondary-button @click="closeCropDialog">{{ $t('crop.cancel') }}</jet-secondary-button>
            <primary-button @click="handleUpload" class="ml-2">{{ $t('crop.upload') }}</primary-button>
        </template>
    </jet-dialog-modal>

</template>


<script>
import { defineComponent, ref } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetDialogModal from "@/Jetstream/DialogModal";
import JetFormSection from "@/Jetstream/FormSection";
import PageTitle from "@/Shared/Components/PageTitle";
import ModalContent from "@/Shared/Components/ModalContent";
import OptionImage from "@/Pages/Advisor/Option/Components/OptionImage";
import { CloudUploadIcon } from "@heroicons/vue/outline";

export default defineComponent({
    props: ["uploadLink", "images", "set"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetSecondaryButton,
        JetDialogModal,
        ModalContent,
        OptionImage,
        CloudUploadIcon
    },
    data() {
        return {
            localImages: this.images,
            showCropDialog: false,
            cropImageSrc: null,
            originalFile: null,
            cropBox: { x: 0, y: 0 },
            displayCropWidth: 0,
            displayCropHeight: 0,
            cropToggle: true
        };
    },
    computed: {
        isCropEnabled() {
            return this.$page.props.crop_mode === 'On';
        },
        maxSizeInMb() {
            const bytes = this.$page.props.branding?.max_option_image_upload_size || (20 * 1024 * 1024);
            return Math.round(bytes / 1024 / 1024);
        },
        cropWidth() {
            return this.$page.props.crop_width || 500;
        },
        cropHeight() {
            return this.$page.props.crop_height || 500;
        }
    },
    methods: {
        cancel() {
            this.$inertia.visit(this.route("advisors.option-sets.show", this.set.uuid), {
                preserveState: false
            });
        },
        setDefault(image) {
            axios.put(image.makeDefaultLink).then((rsp) => {
                this.localImages = rsp.data.images;
                this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "options.imageMadeDefault"
                });
            });
        },
        deleteImage(image) {
            axios.delete(image.deleteLink).then((rsp) => {
                this.localImages = rsp.data.images;
                this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "options.imageRemoved"
                });
            });
        },
        handleFileSelect() {
            const file = this.$refs.image.files[0];
            if (!file) return;

            const fsize = file.size;
            const maxBytes = this.$page.props.branding?.max_option_image_upload_size || (20 * 1024 * 1024);

            if (fsize > maxBytes) {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "options.fileTooBig"
                });
                this.$refs.image.value = null;
                return;
            }

            if (this.isCropEnabled) {
                this.originalFile = file;
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.cropImageSrc = e.target.result;
                    this.cropToggle = false;
                    this.showCropDialog = true;
                };
                reader.readAsDataURL(file);
            } else {
                this.upload(file, 'No');
            }
        },
        initCropBox() {
            const img = this.$refs.cropImage;
            if (!img) return;
            
            const ratio = img.naturalWidth / img.clientWidth;
            
            // Adjust display crop box size based on displayed image width
            this.displayCropWidth = this.cropWidth / ratio;
            this.displayCropHeight = this.cropHeight / ratio;

            // If the crop box is larger than the image, scale it down proportionally to fit
            if (this.displayCropWidth > img.clientWidth || this.displayCropHeight > img.clientHeight) {
                const scale = Math.min(img.clientWidth / this.displayCropWidth, img.clientHeight / this.displayCropHeight);
                this.displayCropWidth *= scale * 0.9; // 90% of fit to be safe
                this.displayCropHeight *= scale * 0.9;
            }

            // Center the crop box
            this.cropBox.x = (img.clientWidth - this.displayCropWidth) / 2;
            this.cropBox.y = (img.clientHeight - this.displayCropHeight) / 2;
        },
        startDragging(e) {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const initialX = this.cropBox.x;
            const initialY = this.cropBox.y;

            const move = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;
                
                const container = this.$refs.cropImage;
                const maxX = container.clientWidth - this.displayCropWidth;
                const maxY = container.clientHeight - this.displayCropHeight;

                this.cropBox.x = Math.max(0, Math.min(maxX, initialX + dx));
                this.cropBox.y = Math.max(0, Math.min(maxY, initialY + dy));
            };

            const up = () => {
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
            };

            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        },
        startResizing(handle, e) {
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const initialX = this.cropBox.x;
            const initialY = this.cropBox.y;
            const initialWidth = this.displayCropWidth;
            const initialHeight = this.displayCropHeight;
            const aspectRatio = initialWidth / initialHeight;
            const container = this.$refs.cropImage;

            const move = (moveEvent) => {
                const dx = moveEvent.clientX - startX;
                const dy = moveEvent.clientY - startY;

                let newWidth, newHeight, newX, newY;

                if (handle === 'se') {
                    // Fix top-left (initialX, initialY)
                    newWidth = Math.max(50, initialWidth + dx);
                    newHeight = newWidth / aspectRatio;
                    
                    if (initialX + newWidth > container.clientWidth) {
                        newWidth = container.clientWidth - initialX;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (initialY + newHeight > container.clientHeight) {
                        newHeight = container.clientHeight - initialY;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = initialX;
                    newY = initialY;

                } else if (handle === 'sw') {
                    // Fix top-right (initialX + initialWidth, initialY)
                    const fixedRight = initialX + initialWidth;
                    newWidth = Math.max(50, initialWidth - dx);
                    newHeight = newWidth / aspectRatio;
                    
                    if (fixedRight - newWidth < 0) {
                        newWidth = fixedRight;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (initialY + newHeight > container.clientHeight) {
                        newHeight = container.clientHeight - initialY;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = fixedRight - newWidth;
                    newY = initialY;

                } else if (handle === 'ne') {
                    // Fix bottom-left (initialX, initialY + initialHeight)
                    const fixedBottom = initialY + initialHeight;
                    newHeight = Math.max(50, initialHeight - dy);
                    newWidth = newHeight * aspectRatio;
                    
                    if (initialX + newWidth > container.clientWidth) {
                        newWidth = container.clientWidth - initialX;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (fixedBottom - newHeight < 0) {
                        newHeight = fixedBottom;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = initialX;
                    newY = fixedBottom - newHeight;

                } else if (handle === 'nw') {
                    // Fix bottom-right (initialX + initialWidth, initialY + initialHeight)
                    const fixedRight = initialX + initialWidth;
                    const fixedBottom = initialY + initialHeight;
                    
                    newHeight = Math.max(50, initialHeight - dy);
                    newWidth = newHeight * aspectRatio;
                    
                    if (fixedRight - newWidth < 0) {
                        newWidth = fixedRight;
                        newHeight = newWidth / aspectRatio;
                    }
                    if (fixedBottom - newHeight < 0) {
                        newHeight = fixedBottom;
                        newWidth = newHeight * aspectRatio;
                    }
                    newX = fixedRight - newWidth;
                    newY = fixedBottom - newHeight;
                }

                this.displayCropWidth = newWidth;
                this.displayCropHeight = newHeight;
                this.cropBox.x = newX;
                this.cropBox.y = newY;
            };

            const up = () => {
                window.removeEventListener('mousemove', move);
                window.removeEventListener('mouseup', up);
            };

            window.addEventListener('mousemove', move);
            window.addEventListener('mouseup', up);
        },
        handleUpload() {
            if (this.cropToggle) {
                this.applyCrop();
            } else {
                this.uploadOriginal();
            }
        },
        applyCrop() {
            const img = this.$refs.cropImage;
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const ratio = img.naturalWidth / img.clientWidth;

            const sourceWidth = this.displayCropWidth * ratio;
            const sourceHeight = this.displayCropHeight * ratio;

            canvas.width = sourceWidth;
            canvas.height = sourceHeight;

            // Use high-quality image smoothing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(
                img,
                this.cropBox.x * ratio,
                this.cropBox.y * ratio,
                sourceWidth,
                sourceHeight,
                0,
                0,
                sourceWidth,
                sourceHeight
            );

            canvas.toBlob((blob) => {
                const file = new File([blob], this.originalFile.name, { type: 'image/jpeg' });
                this.upload(file, 'Yes');
                this.showCropDialog = false;
            }, 'image/jpeg', 0.95);
        },
        uploadOriginal() {
            if (this.originalFile) {
                this.upload(this.originalFile, 'No');
                this.showCropDialog = false;
            }
        },
        upload(file, isCropped = 'No') {
            let formData = new FormData();
            formData.append("image", file || this.$refs.image.files[0]);
            formData.append("is_croped", isCropped);

            axios.post(this.uploadLink, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            }).then((rsp) => {
                this.localImages = rsp.data.images;
                this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "options.imageAdded"
                });
                this.$refs.image.value = null;
            }).catch(error => {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "options.fileTooBig"
                });
                this.$refs.image.value = null;
            });
        },
        selectNewPhoto() {
            this.$refs.image.click();
        },
        closeCropDialog() {
            this.showCropDialog = false;
            this.$refs.image.value = null;
        }
    }

});
</script>

<style scoped></style>

<style scoped></style>
